import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from '@shared/services/shared.service';
import { IAddress } from 'app/models/address/address';
import { IAddressType } from 'app/models/address/address-type';
import { IStatus } from 'app/models/licenses/status';
import { AddressService } from 'app/services/address/address.service';

@Component({
  selector: 'address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss']
})
export class AddressComponent implements OnInit, OnDestroy {
  @Input() entityId: number = 0;
  @Input() entityType: string = '';
  @Input() limitEntries: boolean = false;
  @Input() limitTo: number = 2;
  @Input() statuses: IStatus[] = [];
  @Input() isChange: boolean = false;
  @Input() isEditable: boolean = true;
  @Output() addressesLoaded: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() updatedPhysicalAddress: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() reportDistance: EventEmitter<number> = new EventEmitter<number>();
  @Output() reportOutOfState: EventEmitter<boolean> = new EventEmitter<boolean>();

  public addressTypes: IAddressType[] = [];
  public addresses: IAddress[] = [];
  public address: IAddress = {
    id: 0,
    state: '',
    city: '',
    street: '',
    zip: '',
    county: '',
    archived: false,
    parentId: 0,
    addressTypes: []
  }
  public includeArchived: boolean = false;
  public addingAddress: boolean = false;
  public addressForm = new FormGroup({
    state: new FormControl('', Validators.required),
    city: new FormControl('', Validators.required),
    street: new FormControl('', Validators.required),
    zip: new FormControl('', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]),
    county: new FormControl(''),
    types: new FormControl<IAddressType[] | null>([], Validators.required)
  });

  public addressColumns: string[] = ['street', 'city', 'state', 'zip', 'county', 'types'];
  public addressSource = new MatTableDataSource<IAddress>(this.addresses);
  @ViewChild('addressPaginator', { static: false })
  set addressPaginator(value: MatPaginator | undefined) {
    // Conditional views recreate these controls independently of data loading.
    this.addressSource.paginator = value ?? null;
  }
  get addressPaginator(): MatPaginator {
    return this.addressSource.paginator!;
  }
  @ViewChild('addressSort', { static: false })
  set addressSort(value: MatSort | undefined) {
    // Conditional views recreate these controls independently of data loading.
    this.addressSource.sort = value ?? null;
  }
  get addressSort(): MatSort {
    return this.addressSource.sort!;
  }

  public sharedService = inject(SharedService);
  public addressService = inject(AddressService);

  ngOnInit(): void {
    this.getAddresses(this.includeArchived);
    this.getAddressTypes();
  }

  ngOnDestroy(): void {
    if (this.addingAddress) {
      if (this.address.id > 0) {
        this.saveAddress();
      }
      else if (this.addressForm.dirty) {
        this.createAddress();
      }
    }
  }

  getAddresses(includeArchived: boolean): void {
    this.includeArchived = includeArchived;
    this.addressService.getAddresses(this.entityId, this.entityType, includeArchived).subscribe({
      next: response => this.addresses = response,
      error: e => {
        this.sharedService.toastr.error("Could not get addresses");
        console.log('Get addresses error', e);
      },
      complete: () => {
        this.updateAddressTable();
        this.addressesLoaded.emit(true);
        this.reportOutOfStateAddress();
        if (this.isChange && this.addresses.length === 0) {
          this.toggleAddressForm(true);
        }
      }
    });
  }

  getAddressTypes(): void {
    this.addressService.getAddressTypes(false).subscribe({
      next: response => this.addressTypes = response,
      error: e => {
        this.sharedService.toastr.error("Could not get address types");
        console.log('Get address types error', e)
      }
    });
  }

  validateAddresses(): string[] {
    const errors: string[] = [];

    if (this.addressForm) {
      // Check if required address fields are filled
      if (!this.addressForm.get('street')?.value) {
        errors.push('Street address is required.');
      }

      if (!this.addressForm.get('city')?.value) {
        errors.push('City is required.');
      }

      if (!this.addressForm.get('state')?.value) {
        errors.push('State is required.');
      }

      if (!this.addressForm.get('zipCode')?.value) {
        errors.push('Zip code is required.');
      } else {
        // Validate zip code format
        const zipPattern = /^\d{5}(-\d{4})?$/;
        if (!zipPattern.test(this.addressForm.get('zipCode')?.value ?? '')) {
          errors.push('Zip code must be in format 12345 or 12345-6789.');
        }
      }

      // Add any other address-specific validations
      if (this.addressForm.invalid) {
        errors.push('Please complete all required address fields.');
      }
    } else {
      errors.push('Address information is required.');
    }

    return errors;
  }

  updateAddressTable(): void {
    this.syncAddressTable();
  }

  private syncAddressTable(): void {
    this.addressSource.data = this.addresses ?? [];
  }

  getPhysicalAddress(addresses: IAddress[]): IAddress {
    return addresses.find(a => !a.archived && a.addressTypes.find(t => t.type === 'Physical')) ?? this.getEmptyAddress();
  }

  reportOutOfStateAddress(): void {
    this.reportOutOfState.emit(this.addresses.some(a => !a.archived && a.state !== 'OR' && a.addressTypes.find(t => t.type === 'Physical')));
  }

  getEmptyAddress(): IAddress {
    let emptyAddress: IAddress = {
      id: 0,
      state: '',
      city: '',
      street: '',
      zip: '',
      county: '',
      archived: false,
      parentId: 0,
      addressTypes: []
    }
    return emptyAddress;
  }

  updateAddressForm(address: IAddress): void {
    this.addressForm.reset({
      state: address.state,
      city: address.city,
      street: address.street,
      zip: address.zip,
      county: address.county,
      types: address.addressTypes
    });
  }

  updateAddress(): void {
    let form = this.addressForm.getRawValue();
    this.address.id = this.address.id;
    this.address.state = form.state ?? '';
    this.address.city = form.city ?? '';
    this.address.street = form.street ?? '';
    this.address.zip = form.zip ?? '';
    this.address.county = form.county ?? '';
    this.address.archived = this.address.archived;
    this.address.parentId = this.entityId;
    this.address.addressTypes = form.types ?? [];
  }

  compareFn(type1: IAddressType, type2: IAddressType): boolean {
    return type1.id === type2.id;
  }

  toggleAddressForm(adding: boolean): void {
    this.addingAddress = adding;
    this.updateAddressForm(this.getEmptyAddress());
    if (!adding) {
      this.updateAddressTable();
      this.address = this.getEmptyAddress();
    }
  }

  editAddress(address: IAddress): void {
    this.addingAddress = true;
    this.address = address;
    this.updateAddressForm(address);
  }

  createAddress(): void {
    if (this.limitEntries && this.addresses.length > this.limitTo) {
      return;
    }
    this.updateAddress();
    this.addressService.createAddress(this.address, this.entityId, this.entityType).subscribe({
      next: response => this.addresses.push(response),
      error: e => {
        this.sharedService.toastr.error("Could not create address");
        console.log('create address error', e);
      },
      complete: () => {
        if (this.address.addressTypes.find(t => t.type === 'Physical')) {
          this.updatedPhysicalAddress.emit(true);
          if (this.address.state !== 'OR') {
            this.reportOutOfState.emit(true)
          };
        }
        this.addingAddress = false;
        this.updateAddressTable();
      }
    });
  }

  saveAddress(): void {
    this.updateAddress();
    this.addressService.updateAddress(this.address).subscribe({
      next: () => {
        let index: number = this.addresses.findIndex(a => a.id == this.address.id);
        this.addresses[index] = this.address;
      },
      error: e => {
        this.sharedService.toastr.error("Could not update address");
        console.log('error updating address', e);
      },
      complete: () => {
        if (this.address.addressTypes.find(t => t.type === 'Physical')) {
          this.updatedPhysicalAddress.emit(true);
          if (this.address.state !== 'OR') {
            this.reportOutOfState.emit(true)
          }
          else {
            this.reportOutOfState.emit(false);
          }
        }
        this.address = this.getEmptyAddress();
        this.toggleAddressForm(false);
      }
    });
  }

  archiveAddress(address: IAddress, archive: boolean): void {
    let archiving: string = archive ? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this address?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.addressService.archiveAddress(address.id, archive).subscribe({
            complete: () => {
              address.archived = archive;
              if (archive) {
                this.addresses = this.addresses.filter(a => a.id !== address.id);
              }
              this.toggleAddressForm(false);
            },
            error: e => {
              this.sharedService.toastr.error('Could not archive address');
              console.log('Archive address error', e);
            }
          });
        }
      }
    );
  }

  deleteAddress(address: IAddress): void {
    this.sharedService.openConfirm('Delete the ' + address.street + ' address?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.addressService.deleteAddress(address.id).subscribe({
            complete: () => {
              this.addresses = this.addresses.filter(a => a.id !== address.id);
              this.toggleAddressForm(false);
              this.updateAddressTable();
            },
            error: e => {
              this.sharedService.toastr.error('Could not delete address');
              console.log('Delete address error', e);
            }
          });
        }
      }
    );
  }

  getDistanceBetweenAddressess(fromAddress: IAddress, toAddress: IAddress): void {
    this.addressService.getDistanceBetweenAddresses(fromAddress, toAddress).subscribe({
      next: response => this.reportCalculatedDistance(response),
      error: e => {
        console.log('error getting distance', e);
        this.sharedService.toastr.error('Error getting distance');
      }
    });
  }

  private reportCalculatedDistance(response: number): void {
    if (response === 0) {
      this.sharedService.openNotice('The distance between the entities could not be calculated.');
    }
    else {
      this.reportDistance.emit(response);
    }
  }

  public get locked(): boolean {
    return !this.statuses.some(s => s.label === this.sharedService.started) && !this.isEditable;
  }

  public get approved(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.approved);
  }
}
